import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [googleConfig, setGoogleConfig] = useState({ google_enabled: false, google_client_id: "" });
  const [settings, setSettings] = useState({ year_start_month: 1, work_start: "08:00", work_end: "18:00" });

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem("vb_token");
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
    } catch (e) {
      localStorage.removeItem("vb_token");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    api.get("/auth/config").then(({ data }) => setGoogleConfig(data)).catch(() => {});
    // CRITICAL: if returning from Emergent OAuth (session_id in URL hash),
    // let AuthCallback exchange it first — skip the /me check to avoid a race.
    if (window.location.hash && window.location.hash.includes("session_id=")) {
      setLoading(false);
      return;
    }
    loadMe();
  }, [loadMe]);

  useEffect(() => {
    if (user) {
      api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
    }
  }, [user]);

  // Accepts either a number (legacy: year_start_month) or a patch object
  // ({ year_start_month, work_start, work_end }).
  const updateSettings = async (patch) => {
    const body = typeof patch === "object" && patch !== null ? patch : { year_start_month: patch };
    const { data } = await api.post("/settings", body);
    setSettings(data);
    return data;
  };

  const devLogin = async () => {
    const { data } = await api.post("/auth/dev-login", {});
    localStorage.setItem("vb_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password, origin: window.location.origin });
    localStorage.setItem("vb_token", data.token);
    setUser(data.user);
    return data;
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("vb_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const resendVerification = async () => {
    const { data } = await api.post("/auth/resend-verification", { origin: window.location.origin });
    return data;
  };

  const verifyEmail = async (token) => {
    const { data } = await api.post("/auth/verify-email", { token });
    if (data.user) setUser((prev) => (prev ? { ...prev, ...data.user } : prev));
    return data;
  };

  const forgotPassword = async (email) => {
    const { data } = await api.post("/auth/forgot-password", { email, origin: window.location.origin });
    return data;
  };

  const resetPassword = async (token, password) => {
    const { data } = await api.post("/auth/reset-password", { token, password });
    if (data.token) { localStorage.setItem("vb_token", data.token); setUser(data.user); }
    return data;
  };

  const updateProfile = async (payload) => {
    const { data } = await api.put("/auth/profile", payload);
    setUser(data.user);
    return data.user;
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const startGoogleLogin = () => {
    const redirectUri = window.location.origin + "/auth/google";
    const params = new URLSearchParams({
      client_id: googleConfig.google_client_id,
      redirect_uri: redirectUri,
      response_type: "code",
      scope: "openid email profile",
      access_type: "offline",
      prompt: "consent",
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };

  const completeGoogleLogin = async (code) => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUri = window.location.origin + "/auth/google";
    const { data } = await api.post("/auth/google", { code, redirect_uri: redirectUri });
    localStorage.setItem("vb_token", data.token);
    setUser(data.user);
    return data.user;
  };

  // ---- Emergent-managed Google Auth ----
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const startEmergentGoogle = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const completeEmergentSession = async (sessionId) => {
    const { data } = await api.post("/auth/emergent-session", { session_id: sessionId });
    localStorage.setItem("vb_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("vb_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, googleConfig, settings, updateSettings, devLogin, register, login, updateProfile, resendVerification, verifyEmail, forgotPassword, resetPassword, startGoogleLogin, completeGoogleLogin, startEmergentGoogle, completeEmergentSession, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
